import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import type {
  MalwareContent,
  MalwareFile,
  MalwareRecord,
} from "@dashboard-v3/api";
import {
  PAGE_SIZE,
  StatusFilter,
  applyMalwareActions,
  fetchMalwareContents,
  fetchMalwareFiles,
  isNew,
  matchesStatus,
} from "./utils";

type ListState = {
  contents: MalwareContent[];
  loading: boolean;
  loadingMore: boolean;
  moreToLoad: boolean;
  error: boolean;
  status: StatusFilter;
  search: string;
  searchTerm: string;
  expandedChecksum: string;
  extraFiles: Record<string, MalwareFile[]>;
  loadingFilesOf: string;
  ignoring: MalwareContent;
  openFile: {
    file: MalwareFile;
    content: MalwareContent;
  };
};

type UpdateStateParams =
  | Partial<ListState>
  | ((prev: ListState) => Partial<ListState>);

// State and actions of the malware list page: loading, search, pagination,
// expanding a content (which marks it as viewed) and the dialogs
export default function useMalwareList() {
  const { t } = useTranslation("mxheroShares");
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<ListState>(initState);
  const latestLoad = useRef(0);

  useEffect(() => {
    load(state.searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchTerm]);

  function updateState(update: UpdateStateParams) {
    setState(prev => {
      return {
        ...prev,
        ...(typeof update === "function" ? update(prev) : update),
      };
    });
  }

  async function load(term: string) {
    const loadId = ++latestLoad.current;
    updateState({
      loading: true,
      error: false,
      expandedChecksum: null,
      extraFiles: {},
    });

    try {
      const results = await fetchMalwareContents({ search: term, offset: 0 });
      if (loadId !== latestLoad.current) return;
      updateState({
        contents: results,
        moreToLoad: results.length === PAGE_SIZE,
      });
    } catch (fetchError) {
      if (loadId !== latestLoad.current) return;
      updateState({ error: true });

      enqueueSnackbar(t("malware.errors.fetch"), { variant: "error" });
    } finally {
      if (loadId === latestLoad.current) updateState({ loading: false });
    }
  }

  async function loadMore() {
    updateState({ loadingMore: true });

    try {
      const results = await fetchMalwareContents({
        search: state.searchTerm,
        offset: state.contents.length,
      });
      updateState(prev => ({
        contents: [...prev.contents, ...results],
        moreToLoad: results.length === PAGE_SIZE,
      }));
    } catch (fetchError) {
      enqueueSnackbar(t("malware.errors.fetch"), { variant: "error" });
    } finally {
      updateState({ loadingMore: false });
    }
  }

  function submitSearch() {
    const term = state.search.trim();
    if (term === state.searchTerm) {
      load(term);
    } else {
      updateState({ searchTerm: term });
    }
  }

  function replaceContent(checksum: string, changes: Partial<MalwareContent>) {
    updateState(prev => ({
      contents: prev.contents.map(content =>
        content.checksum === checksum ? { ...content, ...changes } : content
      ),
    }));
  }

  async function markAsViewed(content: MalwareContent) {
    try {
      const record = await applyMalwareActions(content.checksum, ["viewed"]);
      replaceContent(content.checksum, {
        viewedAt: record.viewedAt,
        viewedBy: record.viewedBy,
      });
    } catch (updateError) {
      enqueueSnackbar(t("malware.errors.markAsViewed"), { variant: "error" });
    }
  }

  function toggle(content: MalwareContent) {
    const expanding = state.expandedChecksum !== content.checksum;
    updateState({ expandedChecksum: expanding ? content.checksum : null });

    if (expanding && isNew(content)) markAsViewed(content);
  }

  async function showAllFiles(content: MalwareContent) {
    updateState({ loadingFilesOf: content.checksum });

    try {
      const files = await fetchMalwareFiles(content.checksum);
      updateState(prev => ({
        extraFiles: { ...prev.extraFiles, [content.checksum]: files },
      }));
    } catch (fetchError) {
      enqueueSnackbar(t("malware.errors.fetchFiles"), { variant: "error" });
    } finally {
      updateState({ loadingFilesOf: null });
    }
  }

  function ignored(content: MalwareContent, record: MalwareRecord) {
    replaceContent(content.checksum, {
      allowedToUse: record.allowedToUse,
      allowedBy: record.allowedBy,
      viewedAt: record.viewedAt,
      viewedBy: record.viewedBy,
    });
    updateState({ ignoring: null });
  }

  const loaded = state.contents ?? [];
  const counts = {
    all: loaded.length,
    blocked: loaded.filter(content => matchesStatus(content, "blocked")).length,
    released: loaded.filter(content => matchesStatus(content, "released"))
      .length,
  };
  const visibleContents = loaded.filter(content =>
    matchesStatus(content, state.status)
  );
  // The open modal shows the latest state of its content (e.g. ignored meanwhile)
  const openContent =
    state.openFile &&
    (loaded.find(c => c.checksum === state.openFile.content.checksum) ??
      state.openFile.content);

  return {
    state,
    counts,
    visibleContents,
    openContent,
    loadMore,
    submitSearch,
    toggle,
    showAllFiles,
    ignored,
    setStatus: (status: StatusFilter) => updateState({ status }),
    setSearch: (search: string) => updateState({ search }),
    openFile: (file: MalwareFile, content: MalwareContent) =>
      updateState({ openFile: { file, content } }),
    closeFile: () => updateState({ openFile: null }),
    startIgnoring: (content: MalwareContent) =>
      updateState({ ignoring: content }),
    cancelIgnoring: () => updateState({ ignoring: null }),
  };
}

function initState(): ListState {
  return {
    contents: null,
    loading: true,
    loadingMore: false,
    moreToLoad: false,
    error: false,
    status: "all",
    search: "",
    searchTerm: "",
    expandedChecksum: null,
    extraFiles: {},
    loadingFilesOf: null,
    openFile: null,
    ignoring: null,
  };
}
